import { useEffect, useRef, useState } from 'react';
import { useModal } from 'hooks/3catinfo/useModal';
import { useViewport } from 'hooks/3catinfo/useViewport';
import { useFocus } from 'hooks/3catinfo/useFocus';
import Boto from 'components/3catinfo/Boto';
import Icona from 'components/3catinfo/ui/Icona';
import ItemVideoReel from 'components/3catinfo/Reels/ItemVideoReel';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import styles from './modalreels.module.scss';

const END_OF_FEED = 'end-of-feed';

const _cleanId = (id) => {
    return id.toString().replace('-cloned-2', '').replace('-cloned', '');
};

const getInfoNavegacio = (items, selectedReel) => {
    return {
        numReelSelected: items?.findIndex((reel) => _cleanId(reel.id) === _cleanId(selectedReel)) + 1,
        numItems: items?.filter((item) => !item.cloned).length
    };
};

const Navegacio = (props) => {
    const { items, selectedReel, handleNav, navRef, prevRef, nextRef } = props;
    const [infoNavegacio, setInfoNavegacio] = useState(getInfoNavegacio(items, selectedReel));

    useEffect(() => {
        if (selectedReel !== END_OF_FEED) {
            setInfoNavegacio(getInfoNavegacio(items, selectedReel));
        }
    }, [items, selectedReel]);

    return (
        <div className={styles.modalContentDesktopNavegacio} ref={navRef}>
            <div className={styles.modalInnerDesktopNavegacio}>
                <div className={styles.modalNavs}>
                    <Boto
                        ref={prevRef}
                        className={styles.modalNav}
                        icona='fletxa_simple_amunt'
                        isIconaDreta={false}
                        isIconaBlanc={false}
                        altIcona='Reel previ'
                        tag='button'
                        mida={48}
                        fons='fonsHoverTransparent'
                        onClick={() => handleNav(true)}
                        tabIndex={0}
                    />
                    <Boto
                        ref={nextRef}
                        className={styles.modalNav}
                        icona='fletxa_simple_avall'
                        isIconaDreta={false}
                        isIconaBlanc={false}
                        altIcona='Reel següent'
                        tag='button'
                        mida={48}
                        fons='fonsHoverTransparent'
                        onClick={() => handleNav(false)}
                        tabIndex={0}
                    />
                </div>
                <div className={styles.modalNavPosicio}>
                    {infoNavegacio.numReelSelected} / {infoNavegacio.numItems}
                </div>
            </div>
        </div>
    );
};

const _getIndex = (items, idSelected) => {
    const index = items.findIndex((item) => item.idRenderer.toString() === idSelected.toString());
    return index < 0 ? 0 : index;
};

const _getItems = (items) => {
    switch (items.length) {
        case 2:
            return [
                ...items.map((item) => ({ ...item, idRenderer: `${item.id}-cloned`, cloned: true })),
                ...items.map((item) => ({ ...item, idRenderer: item.id })),
                ...items.map((item) => ({ ...item, idRenderer: `${item.id}-cloned-2`, cloned: true }))
            ];
        case 3:
            return [
                ...items.map((item) => ({ ...item, idRenderer: `${item.id}-cloned`, cloned: true })),
                ...items.map((item) => ({ ...item, idRenderer: item.id }))
            ];
        default:
            return [...items.map((item) => ({ ...item, idRenderer: item.id }))];
    }
};

const ModalReels = (props) => {
    const { items = [], idSelected, onClickClose } = props;
    const itemsRenderer = _getItems(items);
    const [selectedReel, setSelectedReel] = useState(idSelected);
    const [currentIndex, setCurrentIndex] = useState(_getIndex(itemsRenderer, idSelected));
    const [itemsShow, setItemsShow] = useState(<></>);
    const [visibleItems, setVisibleItems] = useState([]);
    const [announcement, setAnnouncement] = useState('');
    const timerScroll = useRef(null);
    const tancarRef = useRef();
    const navRef = useRef();
    const prevRef = useRef();
    const nextRef = useRef();
    const contentModalReelsRef = useRef();
    const reelsListRef = useRef({});
    const containerModal = useRef(null);
    const scrollReels = useRef({
        isScrolling: false,
        reelActive: idSelected,
        lastTouchPos: 0,
        isFirstRun: true,
        lastDirection: 0,
        touchStartY: 0,
        touchScrollStart: 0,
        touchEndY: 0,
        lastKeyPressed: null,
        tabTimeout: null,
        avoidPlaylistendEvent: false,
        avoidPlaylistendEventTimeout: null
    });

    const endOfFeed = useRef({
        viewedFeeds: [idSelected.toString()],
        startIndexFeed:
            items.length > 2
                ? `${_getIndex(itemsRenderer, `${idSelected}-cloned`)}-endoffeed`
                : `${_getIndex(itemsRenderer, idSelected)}-endoffeed`,
        showEndOfFeed: false,
        isAfterShowEndOfFeed: false,
        feedsAfterEndOfFeed: 0,
        hasShowedEnfOfFeed: false,
        direction: 'next',
        currentIndex: _getIndex(itemsRenderer, idSelected)
    });
    const visibleItemsRef = useRef({});
    const scrollTimeWheel = 1000;
    const scrollTimeTrackpad = 2500;

    const { removeModal } = useModal();
    const { getNextFocusableElement, getFocusableElements } = useFocus();

    const { isOnlyMobile } = useViewport();

    const isFocusInNav = () => {
        return navRef?.current?.contains(document.activeElement);
    };

    const isFocusInClose = () => {
        return tancarRef?.current?.contains(document.activeElement);
    };

    const isFocusInReel = () => {
        return reelsListRef.current['active']?.getReelDiv() === document.activeElement;
    };

    const isFocusInAnyReel = () => {
        return contentModalReelsRef?.current?.contains(document.activeElement);
    };

    const changeFocusOnCurrentReel = () => {
        if (isFocusInAnyReel()) {
            setTimeout(() => {
                reelsListRef.current['active']?.getReelDiv().focus();
            }, 500);
        }
    };

    const showEndOfFeed = () => {
        return endOfFeed.current.showEndOfFeed && !endOfFeed.current.hasShowedEnfOfFeed;
    };

    const nextShowEndOfFeed = () => {
        return endOfFeed.current.showEndOfFeed && endOfFeed.current.hasShowedEnfOfFeed && endOfFeed.current.isAfterShowEndOfFeed;
    };

    const setFeedsAfterEndOfFeed = () => {
        if (endOfFeed.current.showEndOfFeed && endOfFeed.current.hasShowedEnfOfFeed) {
            endOfFeed.current.feedsAfterEndOfFeed++;
        }
    };

    const isPreviousReel = (id) => {
        const index = visibleItemsRef.current.findIndex((reel) => reel.idRenderer === id);
        return index === 0;
    };

    const getVisibleItemsBeforeEndOfFeed = () => {
        const currentIndex = endOfFeed.current.currentIndex;
        const previousIndex = currentIndex === 0 ? itemsRenderer.length - 1 : currentIndex - 1;
        const nextIndex = currentIndex === itemsRenderer.length - 1 ? 0 : currentIndex + 1;
        let visibleItemsTmp = [];
        if (endOfFeed.current.direction === 'prev') {
            visibleItemsTmp = [
                { id: END_OF_FEED, idRenderer: END_OF_FEED },
                itemsRenderer[currentIndex],
                itemsRenderer[nextIndex]
            ];
        } else {
            visibleItemsTmp = [
                itemsRenderer[previousIndex],
                itemsRenderer[currentIndex],
                { id: END_OF_FEED, idRenderer: END_OF_FEED }
            ];
        }

        return visibleItemsTmp;
    };

    const getVisibleItemsAfterEndOfFeed = () => {
        const currentIndex = endOfFeed.current.currentIndex;
        const previousIndex = currentIndex === 0 ? itemsRenderer.length - 1 : currentIndex - 1;
        const nextIndex = currentIndex === itemsRenderer.length - 1 ? 0 : currentIndex + 1;

        return [itemsRenderer[previousIndex], itemsRenderer[currentIndex], itemsRenderer[nextIndex]];
    };

    const rearrangeVisibleItems = () => {
        if (showEndOfFeed()) {
            const visibleItemsTemp = getVisibleItemsBeforeEndOfFeed();
            visibleItemsRef.current = visibleItemsTemp;
            setVisibleItems(visibleItemsTemp);
        } else if (endOfFeed.current.feedsAfterEndOfFeed === 2) {
            const visibleItemsTemp = getVisibleItemsAfterEndOfFeed();
            visibleItemsRef.current = visibleItemsTemp;
            setVisibleItems(visibleItemsTemp);
        }
    };

    const topDivReel = () => {
        const containerScroll = containerModal.current;
        const topDiv = document.querySelector(`[data-reel-slot="1"]`);
        const marginDiv = 0;
        const scrollReelsCurrent = scrollReels.current;
        if (containerScroll && topDiv) {
            if (!scrollReelsCurrent.isFirstRun) {
                containerScroll.scrollTo({
                    top: topDiv.offsetTop - marginDiv,
                    behavior: 'smooth'
                });
            } else {
                containerScroll.scrollTop = topDiv.offsetTop - marginDiv;
            }
        }

        if (scrollReelsCurrent.isFirstRun) scrollReelsCurrent.isFirstRun = false;
    };

    const centerDivReel = () => {
        const containerScroll = containerModal.current;
        const centeredDiv = document.querySelector(`[data-reel-slot="1"]`);
        if (containerScroll && centeredDiv) {
            const containerScrollHeight = containerScroll.clientHeight;
            const centeredDivHeight = centeredDiv.clientHeight;
            if (!scrollReels.current.isFirstRun) {
                containerScroll.scrollTo({
                    top: centeredDiv.offsetTop + centeredDivHeight / 2 - containerScrollHeight / 2,
                    behavior: 'smooth'
                });
            } else {
                containerScroll.scrollTop = centeredDiv.offsetTop + centeredDivHeight / 2 - containerScrollHeight / 2;
            }

            if (scrollReels.current.isFirstRun) scrollReels.current.isFirstRun = false;
        }
    };

    const avoidNextVideo = () => {
        if (scrollReels.current.avoidPlaylistendEvent) return false;
        scrollReels.current.avoidPlaylistendEvent = true;
        scrollReels.current.avoidPlaylistendEventTimeout = setTimeout(() => {
            scrollReels.current.avoidPlaylistendEvent = false;
        }, 100);
        return true;
    };

    const onNextVideo = (isPrevious = false) => {
        if (!avoidNextVideo()) return;
        rearrangeVisibleItems();
        const nextReelId = isPrevious ? visibleItemsRef.current[0].idRenderer : visibleItemsRef.current[2].idRenderer;
        endOfFeed.current.direction = isPrevious ? 'prev' : 'next';
        reelsListRef?.current['active']?.stopVideoReel();
        setSelectedReel(nextReelId);
    };

    const setCurrentReel = (nextReelId) => {
        if (nextReelId !== selectedReel) {
            const isPrevious = isPreviousReel(nextReelId);
            onNextVideo(isPrevious);
        }
    };

    const handleNav = (isPrevious) => {
        if (scrollReels.current.isScrolling) return;
        scrollReels.current.isScrolling = true;

        timerScroll.current = setTimeout(() => {
            scrollReels.current.isScrolling = false;
        }, scrollTimeWheel);
        onNextVideo(isPrevious);
    };

    const handleScroll = (e) => {
        e.preventDefault();
        if (scrollReels.current.isScrolling) return;
        scrollReels.current.isScrolling = true;

        const deltaAbs = Math.abs(e.deltaY);
        const scrollTime = deltaAbs > 60 ? scrollTimeWheel : scrollTimeTrackpad;

        timerScroll.current = setTimeout(() => {
            scrollReels.current.isScrolling = false;
        }, scrollTime);
        let direction = 0;
        direction = e.deltaY > 0 ? 1 : -1;
        onNextVideo(direction < 0);
    };

    const handleTouchStart = (e) => {
        scrollReels.current.touchStartY = e.touches[0].clientY;
        scrollReels.current.touchScrollStart = containerModal.current.scrollTop;
    };

    const handleTouchMove = (e) => {
        const deltaY = e.touches[0].clientY - scrollReels.current.touchStartY;
        containerModal.current.scrollTop = scrollReels.current.touchScrollStart - deltaY * 0.9;

        e.preventDefault();
    };

    const handleTouchEnd = (e) => {
        scrollReels.current.touchEndY = e.changedTouches[0].clientY;
        const deltaY = scrollReels.current.touchEndY - scrollReels.current.touchStartY;

        if (Math.abs(deltaY) > 50) {
            onNextVideo(deltaY > 0);
        }
    };

    const handleKeyDownDocument = (e) => {
        if (e.key === 'Tab') {
            if (reelsListRef.current['active']?.getReelDiv()?.contains(document.activeElement)) {
                if (
                    !reelsListRef.current['active']
                        ?.getReelDiv()
                        ?.contains(getNextFocusableElement(containerModal?.current, e.shiftKey))
                ) {
                    e.preventDefault();
                    if (e.shiftKey) {
                        prevRef.current.focus();
                    } else {
                        nextRef.current.focus();
                    }
                }
            } else if (isFocusInNav()) {
                e.preventDefault();

                if (document.activeElement === prevRef.current) {
                    if (e.shiftKey) {
                        tancarRef?.current?.focus();
                    } else {
                        reelsListRef.current['active']?.getReelDiv().focus();
                    }
                } else if (document.activeElement === nextRef.current) {
                    if (e.shiftKey) {
                        reelsListRef.current['active']?.getReelDiv()?.querySelector('[data-type="compartir"]')?.focus();
                    } else {
                        tancarRef?.current?.focus();
                    }
                } else {
                    if (e.shiftKey) {
                        if (scrollReels.current.reelActive === END_OF_FEED) {
                            reelsListRef.current['active']?.getReelDiv()?.querySelector('button').focus();
                        } else {
                            reelsListRef.current['active']?.getReelDiv()?.querySelector('[data-type="compartir"]')?.focus();
                        }
                    }
                }
            } else if (isFocusInClose()) {
                e.preventDefault();
                if (e.shiftKey) {
                    nextRef?.current?.focus();
                } else {
                    prevRef?.current?.focus();
                }
            }

            scrollReels.current.lastKeyPressed = e.shiftKey ? 'prev' : 'next';
        }

        if (e.key === 'Escape') {
            removeModal();
            e.stopImmediatePropagation();
        }
        if (e.key === 'Enter') {
            e.stopImmediatePropagation();
        }
    };

    const handleFocusin = (e) => {
        if (isFocusInReel()) {
            scrollReels.current.tabTimeout = setTimeout(() => {
                if (scrollReels.current.lastKeyPressed === 'next') {
                    reelsListRef.current['active']?.getReelDiv()?.querySelector('.bmpui-ui-hugeplaybacktogglebutton').focus();
                } else if (scrollReels.current.lastKeyPressed === 'prev') {
                    prevRef?.current?.focus();
                }
            }, 0);
        }
    };

    const getVisibleItems = (currentIndex, previousIndex, nextIndex) => {
        let visibleItems = [];

        if (nextShowEndOfFeed()) {
            visibleItems =
                endOfFeed.current.direction === 'prev'
                    ? [itemsRenderer[previousIndex], itemsRenderer[currentIndex], { id: END_OF_FEED, idRenderer: END_OF_FEED }]
                    : [{ id: END_OF_FEED, idRenderer: END_OF_FEED }, itemsRenderer[currentIndex], itemsRenderer[nextIndex]];
            endOfFeed.current.isAfterShowEndOfFeed = false;
        } else if (showEndOfFeed()) {
            visibleItems = [
                itemsRenderer[previousIndex],
                { id: END_OF_FEED, idRenderer: END_OF_FEED },
                itemsRenderer[currentIndex]
            ];
            endOfFeed.current.hasShowedEnfOfFeed = true;
            endOfFeed.current.isAfterShowEndOfFeed = true;
        } else {
            visibleItems =
                itemsRenderer.length < 3
                    ? itemsRenderer
                    : [itemsRenderer[previousIndex], itemsRenderer[currentIndex], itemsRenderer[nextIndex]];
        }

        return visibleItems;
    };

    useEffect(() => {
        const scrollReelsCurrent = scrollReels.current;
        containerModal.current = document.getElementById('container-modal');

        containerModal.current.addEventListener('wheel', handleScroll, { passive: false });
        containerModal.current.addEventListener('touchstart', handleTouchStart, { passive: true });
        containerModal.current.addEventListener('touchmove', handleTouchMove, { passive: false });
        containerModal.current.addEventListener('touchend', handleTouchEnd, { passive: true });
        document.addEventListener('keydown', handleKeyDownDocument, true);
        document.addEventListener('focusin', handleFocusin);
        setTimeout(() => {
            const focusables = getFocusableElements(containerModal.current);
            focusables?.[0]?.focus();
        }, 150);

        return () => {
            containerModal.current.removeEventListener('wheel', handleScroll, { passive: false });
            containerModal.current.removeEventListener('touchstart', handleTouchStart, { passive: true });
            containerModal.current.removeEventListener('touchmove', handleTouchMove, { passive: false });
            containerModal.current.removeEventListener('touchend', handleTouchEnd, { passive: true });
            document.removeEventListener('keydown', handleKeyDownDocument, true);
            document.removeEventListener('focusin', handleFocusin);
            clearTimeout(timerScroll.current);
            clearTimeout(scrollReelsCurrent.avoidPlaylistendEventTimeout);
            clearTimeout(scrollReelsCurrent.tabTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Array.isArray(itemsShow)) {
            scrollReels.current.reelActive = selectedReel;
            reelsListRef?.current['active']?.playVideoReel();

            if (isOnlyMobile) {
                topDivReel();
            } else {
                centerDivReel();
            }

            if (scrollReels.current.isFirstRun) scrollReels.current.isFirstRun = false;
            changeFocusOnCurrentReel();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsShow]);

    useEffect(() => {
        if (!__isEmpty(visibleItems)) {
            setItemsShow(
                visibleItems.map((item, slot) => {
                    const active = slot === 1;
                    const idRenderer = item.idRenderer;
                    return (
                        <ItemVideoReel
                            key={`item-modal-reel-${idRenderer}`}
                            ref={(element) => (reelsListRef.current[active ? 'active' : idRenderer] = element)}
                            active={active}
                            element={item}
                            slot={slot}
                            numTotalItems={items.length}
                            onNextVideo={onNextVideo}
                            setCurrentReel={setCurrentReel}
                        />
                    );
                })
            );
        }
        visibleItemsRef.current = visibleItems;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleItems]);

    useEffect(() => {
        let currentIndexClean = parseInt(currentIndex.toString().replace('-endoffeed', ''));
        const previousIndex = currentIndexClean === 0 ? itemsRenderer.length - 1 : currentIndexClean - 1;
        const nextIndex = currentIndexClean === itemsRenderer.length - 1 ? 0 : currentIndexClean + 1;
        const visibleItemsTemp = getVisibleItems(currentIndexClean, previousIndex, nextIndex);
        visibleItemsRef.current = visibleItemsTemp;
        setVisibleItems(visibleItemsTemp);

        endOfFeed.current.showEndOfFeed = endOfFeed.current.viewedFeeds.length === items.length;
        endOfFeed.current.prevEndOfFeed = endOfFeed.current.viewedFeeds.length === items.length - 1;
        setFeedsAfterEndOfFeed();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    useEffect(() => {
        const newIndex = selectedReel === END_OF_FEED ? endOfFeed.current.startIndexFeed : _getIndex(itemsRenderer, selectedReel);
        endOfFeed.current.currentIndex = newIndex;
        setCurrentIndex(newIndex);
        handleUpdate(newIndex);

        if (selectedReel !== END_OF_FEED) {
            endOfFeed.current.viewedFeeds = [...new Set([...endOfFeed.current.viewedFeeds, _cleanId(selectedReel)])];
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedReel]);

    const handleUpdate = (index) => {
        setAnnouncement(`Nou contingut, notícia ${index + 1}`);
        setTimeout(() => {
            setAnnouncement('');
        }, 1000);
    };

    return (
        <div>
            <>
                <div className={styles.controls}>
                    <div className={styles.controlsInner}>
                        <button className={styles.botoTancar} onClick={onClickClose} ref={tancarRef}>
                            <Icona className={styles.iconaTancar} icona='tanca' alt='tanca' mida='32' isBlanc={true} />
                        </button>
                    </div>
                </div>
                <div className={styles.contentModalReels} ref={contentModalReelsRef}>
                    {itemsShow}
                </div>
                <Navegacio
                    items={itemsRenderer}
                    selectedReel={selectedReel}
                    handleNav={handleNav}
                    navRef={navRef}
                    prevRef={prevRef}
                    nextRef={nextRef}
                />
            </>
            <div aria-live='assertive' className={styles.announcement}>
                {announcement}
            </div>
        </div>
    );
};

export default ModalReels;
